import { yupResolver } from '@hookform/resolvers/yup'
import { Grid } from '@mui/material'
import React, { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useBoolean } from 'react-use'

import PageSection from 'components/common/PageSection'
import {
  AuthenticationFormValues,
  getAuthenticationFormValidationSchema,
  getDefaultAuthenticationFormValues
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolAuthentication } from 'components/status-checks/form/authentication/ObservabilityToolAuthentication'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const CreateAuthenticationForm: React.FC = () => {
  const [isAuthenticationSaving, setIsAuthenticationSaving] = useBoolean(false)

  const {
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const unsavedExternalIntegrations = getAvailableExternalIntegrations(true)
  const allExternalIntegrations = getAvailableExternalIntegrations(false)

  const authenticationFormValidationSchema =
    getAuthenticationFormValidationSchema(allExternalIntegrations)

  // Adds the based AWS integration to the unsaved integrations list
  const externalIntegrations = useMemo(() => {
    const awsIntegrations = allExternalIntegrations.filter(
      (integration) =>
        integration.type === IntegrationType.AWS && !integration.isConfigured
    )

    return [...unsavedExternalIntegrations, ...awsIntegrations]
  }, [allExternalIntegrations, unsavedExternalIntegrations])

  const defaultValues = getDefaultAuthenticationFormValues()

  const form = useForm<AuthenticationFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(authenticationFormValidationSchema)
  })

  return (
    <PageSection
      hasError={hasErrorExternalIntegrations}
      isLoading={isFetchingExternalIntegrations}
    >
      <FormProvider {...form}>
        <Grid container>
          <Grid item lg={9} xs={12}>
            <ObservabilityToolAuthentication
              externalIntegrations={externalIntegrations}
              isAuthenticationFormValid={form.formState.isValid}
              isAuthenticationSaving={isAuthenticationSaving}
              setIsAuthenticationSaving={setIsAuthenticationSaving}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </PageSection>
  )
}
